// Entry point. The only file that names the deployment's identity, and the only place the whole
// dependency graph is assembled. Everything it wires has already been tested in isolation; this is
// the composition root, deliberately kept small enough to read in one sitting.

import { createWebAuthn } from "../webauthn_2026-08-27/webauthn.js"
import { createRemoteStorage } from "../remote-storage_2026-08-27/remote-storage.js"
import { createSession } from "../session_2026-08-27/session.js"
import { mount, createIndexedDbCheckpoints } from "./app.js"

/**
 * Build-time identity. Spec §8.1: the RP ID is baked in, must be the full host, and cannot be
 * changed after the first credential exists — moving it destroys every vault.
 *
 * These are intentionally not defaulted. A build that forgets to set them must fail loudly here
 * rather than enrol credentials against the wrong origin, because that mistake is silent and
 * unrecoverable.
 */
const env = import.meta.env as Record<string, string | undefined>
const RP_ID = env["VITE_VAULT_RP_ID"]
const ORIGIN = env["VITE_VAULT_ORIGIN"]
const API_BASE = env["VITE_VAULT_API"] ?? ""

const root = document.getElementById("root")
if (!root) throw new Error("missing #root")

function fatal(message: string): never {
  const pre = document.createElement("div")
  pre.className = "centre"
  const card = document.createElement("div")
  card.className = "card"
  const heading = document.createElement("h1")
  heading.textContent = "This build is misconfigured"
  const body = document.createElement("p")
  body.textContent = message
  card.append(heading, body)
  pre.append(card)
  root!.replaceChildren(pre)
  throw new Error(message)
}

if (!RP_ID || !ORIGIN) {
  fatal(
    "VITE_VAULT_RP_ID and VITE_VAULT_ORIGIN must be set at build time. Refusing to run: " +
      "enrolling against the wrong origin would create credentials that can never open this vault.",
  )
}

const webauthn = createWebAuthn({ rpId: RP_ID, rpName: "Vault", origin: ORIGIN })

const remote = createRemoteStorage({
  baseUrl: API_BASE,
  checkpoints: createIndexedDbCheckpoints(),
})

mount(root, { session: createSession({ webauthn, remote }) })
